import React, { useEffect, useState } from "react";
import PageTemplate from "../../../Templates/PageTemplate";
import { useNavigate } from "react-router";
import { useLocation } from "react-router-dom";
import Cookies from 'universal-cookie';
import middleware from "../../../Api/Middleware";
import { BorderedTextInput } from "../../../HouseStyle/Components/Inputs";
import { BorderedDropDown } from "../../../HouseStyle/Components/DropDowns";
import TextEditor from "../../../Main/TextEditor";
import { BackButton } from "../../../HouseStyle/Components/Buttons";
import Placeholder from "../../Icons/empty-placeholder.jpg";
import MediaUploaderComponent from "../../../MediaUpload/MediaUploaderComponent";
import NormalMediaUploader from "../../../MediaUpload/NormalMediaUpload/NormalMediaUpload";
import ToastSuccess from "../../../Toasts/ToastSuccess";

const HouseStyle = () => {
  const location = useLocation();
  const data = location.state?.companyInfo;
  const [loading, setLoading] = useState(true);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [uploadedImageTermisch, setUploadedImageTermisch] = useState(null);
  const [companyInfo, setCompanyInfo] = useState({
    setting_id: null,
    logo: null,
    blackwhite_logo: null,
    font: ''
  });
  const [companyInfoOld, setCompanyInfoOld] = useState({});
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Navigation setup
  const cookies = new Cookies();
  const navigate = useNavigate();
  const [modals, setModals] = useState({
    deactivateCompanyInfo: false
  });
  const toggleModal = (modal) => setModals((prev) => ({ ...prev, [modal]: !prev[modal] }));

  // Helper function to construct full image URL
  const getFullImageUrl = (logo) => {
    if (!logo) return null;
    if (logo.startsWith('http') || logo.startsWith('data:')) return logo;
    return `${middleware.defaults.imagesBaseURL}${logo}`;
  };

  useEffect(() => {
    const loadCompanyInfo = async () => {
      try {
        let fetchedInfo = null;

        if (data && Object.keys(data).length > 0) {
          fetchedInfo = data;
        } else {
          const response = await middleware.get('/settings/backoffice');
          fetchedInfo = response?.data?.data?.[0];
        }

        if (fetchedInfo) {
          const cleanData = {
            setting_id: fetchedInfo.setting_id,
            logo: fetchedInfo.logo,
            blackwhite_logo: fetchedInfo.blackwhite_logo,
            font: fetchedInfo.font?.trim() || ''
          };
          
          setCompanyInfo(cleanData);
          setCompanyInfoOld(cleanData);
          
          // Set initial uploaded images with full URLs for display
          if (cleanData.logo) {
            setUploadedImage({ url: getFullImageUrl(cleanData.logo) });
          }
          if (cleanData.blackwhite_logo) {
            setUploadedImageTermisch({ url: getFullImageUrl(cleanData.blackwhite_logo) });
          }
        }
      } catch (error) {
        console.error('Error fetching company information:', error);
      } finally {
        setLoading(false);
        setIsInitialLoad(false);
      }
    };

    loadCompanyInfo();
  }, [data]);


  // When uploadedImage changes, update the logo in companyInfo
  useEffect(() => {
    if (isInitialLoad) return; // Skip during initial load
    
    if (uploadedImage?.file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        if (base64String) {
          setCompanyInfo((prev) => ({ ...prev, logo: base64String }));
        }
      };
      reader.readAsDataURL(uploadedImage.file);
    }
  }, [uploadedImage, isInitialLoad]);

  useEffect(() => {
    if (isInitialLoad) return; // Skip during initial load
    
    if (uploadedImageTermisch?.file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        if (base64String) {
          setCompanyInfo((prev) => ({ ...prev, blackwhite_logo: base64String }));
        }
      };
      reader.readAsDataURL(uploadedImageTermisch.file);
    }
  }, [uploadedImageTermisch, isInitialLoad]);

  // When companyInfo changes, update the backend
  useEffect(() => {
    if (isInitialLoad) return; // Skip during initial load
    if (JSON.stringify(companyInfo) === JSON.stringify(companyInfoOld)) return;
    
    UpdateCompanyInfo();
  }, [companyInfo, isInitialLoad]);

  const UpdateCompanyInfo = async () => {
    try {
      let updated = false;

      if (
        companyInfo.logo !== companyInfoOld.logo ||
        companyInfo.blackwhite_logo !== companyInfoOld.blackwhite_logo
      ) {
        await middleware.put("/settings/backOfficeSettingsLogoOnly", {
          setting_id: companyInfo.setting_id,
          logo: companyInfo.logo,
          blackwhite_logo: companyInfo.blackwhite_logo,
        });
        ToastSuccess('Logo updated successfully');
        updated = true;
      }

      if (companyInfo.font !== companyInfoOld.font) {
        await middleware.put("/settings/backoffice", {
          setting_id: companyInfo.setting_id,
          font: companyInfo.font,
        });
        ToastSuccess('Font updated successfully');
        updated = true;
      }

      // Update state once at the end if anything was updated
      if (updated) {
        setCompanyInfoOld(companyInfo);
      }
    } catch (error) {
      console.error('Error updating company information:', error);
    }
  };

  // Get display URL for images
  const displayLogoUrl = uploadedImage?.url || getFullImageUrl(companyInfo.logo) || Placeholder;
  const displayThermalLogoUrl = uploadedImageTermisch?.url || getFullImageUrl(companyInfo.blackwhite_logo) || Placeholder;

  return (
    <PageTemplate pageIsLoading={loading} navbarTitle={"Bedrijfsinformatie"}>
      <div className="col-12 d-flex flex-column">
        <div className="d-flex mb-4">
          <BackButton size="l" text={"Terug"} onClickFunction={() => navigate("/algemene/instellingen/bedrijfsinformatie")} />
        </div>
        <div className="whiteBox px-5 py-4">
          <h2 className="fw-semibold mt-2">Huisstijl aanpassen</h2>
          <h4 className="fw-bold pt-2">Jouw bedrijf</h4>
          <h6 className="fw-bold py-2 pt-3">Lettertype</h6>
          <div className="col-2">
            <BorderedDropDown 
              size="s" 
              selectedValue={companyInfo.font?.trim() || ""} 
              setSelectedValue={(value) => setCompanyInfo((prev) => ({ ...prev, font: value?.trim() || "" }))} 
              placeholder="e.g. Comic Sans" 
              options={[
                { title: "Nunito", value: "nunito" },
                { title: "Playfair Display", value: "playfair display" },
                { title: "Calibri", value: "calibri" },
                { title: "Arial", value: "arial" }
              ]} 
            />
          </div>
          <h4 className="fw-bold pt-2">Logo</h4>
          <div className="row">
            <div className="col-4 pt-3">
              <p className="fw-bold">Logo</p>
              <img 
                src={displayLogoUrl} 
                alt="Company Logo" 
                width={250} 
                height={150}
                style={{ objectFit: 'contain' }}
              />
              <p className="text-small text-secondary">
                De aanbevolen grootte voor het uploaden van afbeeldingen is 500 bij 300 of hoger.
              </p>
            </div>
            <div className="pdfInput col-8 pt-5 mt-2">
              <NormalMediaUploader 
                onImageChange={setUploadedImage} 
                allowedFormats={["image/png", "image/jpeg", "image/jpg"]} 
              />
            </div>
            <div className="col-4 pt-3">
              <p className="fw-bold">Zwart logo witte achtergrond (voor thermische printers)</p>
              <img 
                src={displayThermalLogoUrl} 
                alt="Thermal Printer Logo" 
                width={250} 
                height={150}
                style={{ objectFit: 'contain' }}
              />
              <p className="text-small text-secondary">
                De aanbevolen grootte voor het uploaden van afbeeldingen is 500 bij 300 of hoger. Alleen PNG wordt ondersteund voor afdrukken in de winkel.
              </p>
            </div>
            <div className="pdfInput col-8 pt-5 mt-2">
              <NormalMediaUploader 
                onImageChange={setUploadedImageTermisch} 
                allowedFormats={["image/png"]} 
              />
            </div>
          </div>
        </div>
      </div>
    </PageTemplate>
  );
};

export default HouseStyle;